/**
 * 裂变优惠券落地页（v3.35）—— /redeem?pid=xxx&ref=0x…
 *
 * 场景：商家分享「带推荐人」的优惠券链接给好友 → 好友打开本页：
 *  1. 展示活动信息（券值 / 剩余 / 商家店铺名）；
 *  2. 若链接带推荐人且当前身份未绑定 → 一键「绑定推荐人」上链（referral bind，
 *     之后好友核销时 8% 推广佣金自动给推荐人，链端 promo_redeem 已实现）；
 *  3. 输入消费金额 → 核销（promoRedeem，五方分账，消费者得 3% 返现）。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { parseDpb } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import { fetchMerchantProfiles, getMyLocation, type MerchantProfile } from '@/lib/merchantApi';
import { LS_REFERRAL_BOUND, markReferralBound } from '@/utils/referral';
import type { Address } from '@shared/chain';

interface PromoRow {
  promo_id: string;
  from: string;
  budget: number;
  remaining: number;
  coupon_value: number;
  max_uses: number;
  used: number;
  expires_height: number;
  promoter_bps: number;
}

export function RedeemPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pid = (params.get('pid') ?? '').trim();
  const ref = (params.get('ref') ?? '').trim().toLowerCase();
  const address = useIdentityStore((s) => s.address) as Address | undefined;
  const status = useIdentityStore((s) => s.status);
  const locked = status === 'locked';

  const [promo, setPromo] = useState<PromoRow | null>(null);
  const [profile, setProfile] = useState<MerchantProfile | undefined>(undefined);
  const [height, setHeight] = useState(0);
  const [amount, setAmount] = useState('1');
  // v3.39 会员积分抵扣：本店积分余额 + 「用积分」开关（链端 use_points，金库补贴）
  const [myPoints, setMyPoints] = useState<number | null>(null);
  const [usePoints, setUsePoints] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const dpbVal = (v: number | undefined): string =>
    Number(v ?? 0).toLocaleString('zh-CN', { maximumFractionDigits: 4 });

  const load = useCallback(async (): Promise<void> => {
    try {
      const [pl, h, profiles] = await Promise.all([
        container.chain.promoList().catch(() => ({ items: [] }) as Record<string, unknown>),
        container.chain.getBlockHeight().catch(() => 0),
        fetchMerchantProfiles().catch(() => [] as MerchantProfile[]),
      ]);
      setHeight(h);
      const items = (pl?.items ?? []) as PromoRow[];
      const found = items.find((x) => x.promo_id === pid) ?? null;
      setPromo(found);
      if (found) {
        setProfile(profiles.find((p) => p.address.toLowerCase() === String(found.from).toLowerCase()));
        // v3.39 本店会员积分（核销页「用积分抵扣」开关的数据源）
        if (address) {
          const pts = await container.chain.pointsOf(address).catch(() => [] as Array<{ merchant: Address; points: number }>);
          const mine = pts.find((x) => x.merchant.toLowerCase() === String(found.from).toLowerCase());
          setMyPoints(mine ? mine.points : 0);
        }
      }
    } catch {
      setPromo(null);
    }
  }, [address, pid]);

  useEffect(() => {
    void load();
  }, [load]);

  const alive = useMemo(
    () =>
      promo !== null &&
      height > 0 &&
      height <= Number(promo.expires_height ?? 0) &&
      Number(promo.used ?? 0) < Number(promo.max_uses ?? 0),
    [promo, height],
  );

  const needBind = useMemo(() => {
    if (!address || !ref) return false;
    if (ref === address.toLowerCase()) return false; // 不能绑自己
    return window.localStorage.getItem(LS_REFERRAL_BOUND) !== '1'; // 本地标记：是否已绑定过
  }, [address, ref]);

  /** 绑定推荐人（链上 referral bind，幂等由链端护栏；成功后本地标记防重复引导）。 */
  const doBind = async (): Promise<void> => {
    if (!address) return;
    setBusy(true);
    try {
      await container.chain.referral({
        from: address,
        referrer: ref as Address,
        signature: container.identity.signObject({ referral: address, referrer: ref, ts: Date.now() }),
      });
      markReferralBound();
      useUiStore.getState().pushToast('success', `已绑定推荐人 ${ref.slice(0, 6)}…${ref.slice(-4)}（核销后他获 8% 佣金）`);
      setMsg('已绑定推荐人，核销后推广佣金将自动分给推荐人。');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
      setMsg('绑定失败（可能已绑定过或链端拒绝），不影响核销。');
    } finally {
      setBusy(false);
    }
  };

  /** 消费核销：五方分账，消费者得 3% 返现。 */
  const doRedeem = async (): Promise<void> => {
    if (!address || !promo) return;
    const amt = parseDpb(amount);
    if (amt === '0') {
      setMsg('请输入核销金额（大于 0）');
      return;
    }
    setBusy(true);
    try {
      // v3.39 地理围栏：尽力取定位（商家开启围栏后链端校验距离；失败不阻断）
      const loc = await getMyLocation().catch(() => undefined);
      // 分享者地址归一：裸 40 位 hex 补 0x（链端 requireAddr 要求 0x+40hex）
      let refAddr: Address | undefined;
      if (ref && ref !== address.toLowerCase()) {
        const r = ref.startsWith('0x') ? ref : (/^[0-9a-f]{40}$/.test(ref) ? `0x${ref}` : undefined);
        if (r) refAddr = r as Address;
      }
      await container.chain.promoRedeem({
        consumer: address,
        promoId: promo.promo_id,
        amountMicro: amt,
        // v3.39 即时分享返佣：链接带 ?ref= 时直接作为本次 promoter（链端无冷静期）
        ref: refAddr,
        usePoints: usePoints && (myPoints ?? 0) > 0,
        lat: loc?.lat,
        lng: loc?.lng,
        signature: container.identity.signObject({
          action: 'promo_redeem',
          from: address,
          promo_id: promo.promo_id,
          ts: Date.now(),
        }),
      });
      useUiStore.getState().pushToast('success', `核销成功：五方分账已执行，你获 3% 返现${usePoints && (myPoints ?? 0) > 0 ? '，积分已抵扣' : ''}`);
      void load();
    } catch (err) {
      setMsg(`核销失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const merchantName = profile?.name ?? (promo ? `商家 ${String(promo.from).slice(0, 6)}…${String(promo.from).slice(-4)}` : '');

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <header className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-text">优惠券核销</h1>
        <Button variant="ghost" size="sm" onClick={() => navigate('/discover')}>
          返回发现
        </Button>
      </header>

      {!promo ? (
        <EmptyState title="优惠券不存在或已失效" description="请向商家索取有效的优惠券链接（promo 推广码）" />
      ) : (
        <>
          <section className="card p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold text-text">{merchantName}</h2>
                <p className="mt-1 text-[11px] text-dim">
                  {profile?.category ? `${profile.category} · ` : ''}
                  {profile?.city ? `${profile.city} · ` : ''}
                  {profile?.addr ? profile.addr : String(promo.from).slice(0, 10) + '…'}
                </p>
              </div>
              {alive ? <Badge tone="ok">可核销</Badge> : <Badge tone="warn">已结束</Badge>}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
              <div className="rounded-btn bg-panel-2 px-3 py-2">
                <div className="text-lg font-semibold text-text">{dpbVal(promo.coupon_value)}</div>
                <div className="text-[11px] text-dim">券值 DPB/次</div>
              </div>
              <div className="rounded-btn bg-panel-2 px-3 py-2">
                <div className="text-lg font-semibold text-text">{promo.used}/{promo.max_uses}</div>
                <div className="text-[11px] text-dim">已核销/上限</div>
              </div>
              <div className="rounded-btn bg-panel-2 px-3 py-2">
                <div className="text-lg font-semibold text-text">{dpbVal(promo.remaining)}</div>
                <div className="text-[11px] text-dim">剩余预算</div>
              </div>
              <div className="rounded-btn bg-panel-2 px-3 py-2">
                <div className="text-lg font-semibold text-text">{((promo.promoter_bps ?? 800) / 100).toFixed(1)}%</div>
                <div className="text-[11px] text-dim">推广佣金</div>
              </div>
            </div>
            {profile?.desc ? <p className="mt-3 text-[11px] leading-relaxed text-dim">{profile.desc}</p> : null}
          </section>

          {needBind ? (
            <section className="card p-5">
              <h2 className="text-sm font-medium text-text">🎁 来自好友的优惠券</h2>
              <p className="mt-1 text-xs leading-relaxed text-dim">
                推荐人 <span className="mono">{ref.slice(0, 6)}…{ref.slice(-4)}</span> 邀请你领取此券。
                绑定推荐人后，你核销时他自动获得 {(promo.promoter_bps ?? 800) / 100}% 推广佣金（不影响你的 3% 返现）。
              </p>
              <Button className="mt-3 w-full" loading={busy} disabled={!address} onClick={() => void doBind()}>
                一键绑定推荐人（上链）
              </Button>
              {!address ? <p className="mt-2 text-[11px] text-dim">请先解锁身份（钱包即身份）</p> : null}
            </section>
          ) : null}

          <section className="card p-5">
            <h2 className="mb-1 text-sm font-medium text-text">到店核销</h2>
            <p className="mb-3 text-xs text-dim">
              输入消费金额，自动按券抵扣，五方分账上链：商家 85% / 推广者 8% / 你 3% / 金库 2% / 燃烧 2%。
            </p>
            {myPoints !== null && myPoints > 0 ? (
              <label className="mb-3 flex cursor-pointer items-center gap-2 rounded-btn bg-panel-2 px-3 py-2">
                <input type="checkbox" checked={usePoints} onChange={(e) => setUsePoints(e.target.checked)} />
                <span className="text-xs text-text">
                  用会员积分抵扣（{myPoints.toLocaleString('zh-CN', { maximumFractionDigits: 2 })} 分 ≈{' '}
                  {(Math.min(myPoints * 0.01, 5)).toLocaleString('zh-CN', { maximumFractionDigits: 2 })} DPB/次上限）
                </span>
              </label>
            ) : null}
            <div className="flex gap-2">
              <input
                className="w-full rounded-btn bg-panel-2 border border-line px-3 py-2 text-sm text-text placeholder-dim outline-none focus:border-accent"
                type="number"
                min="0.000001"
                step="any"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="消费金额 (DPB)"
              />
              <Button className="shrink-0" loading={busy} disabled={!address || !alive} onClick={() => void doRedeem()}>
                核销
              </Button>
            </div>
            {!locked && !address ? <p className="mt-2 text-[11px] text-dim">未解锁身份，无法核销。</p> : null}
            {msg ? <p className="mt-3 text-xs text-accent">{msg}</p> : null}
          </section>
        </>
      )}
    </div>
  );
}

export default RedeemPage;
